import { Suspense, lazy } from 'react';
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { EvaluationResults } from "@/components/practice/EvaluationResults";
import { EvaluationSkeleton } from "@/components/practice/EvaluationSkeleton";
import { useEvaluation } from "@/components/practice/hooks/useEvaluation";
import { supabase } from "@/integrations/supabase/client";
import { Header } from '@/components/landing/Header';
import { Topic } from '@/types/topic';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

const Footer = lazy(() => import("@/components/Footer"));

type Draft = {
  content: string;
  topic_id: string | null;
};

const PageLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-screen flex-col bg-stone-50 dark:bg-stone-950">
    <Header />
    <main className="flex-grow px-4 py-8 max-w-6xl container mx-auto space-y-8">
      {children}
    </main>
    <Suspense fallback={<div className="h-16" />}>
      <Footer />
    </Suspense>
  </div>
);

const LoadingState = () => (
  <PageLayout>
    <div className="space-y-8">
      <div className="space-y-4 bg-white dark:bg-stone-800 p-6 rounded-lg border border-stone-200 dark:border-stone-700">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <h1 className="text-3xl font-semibold dark:text-white">
        Shared Essay Evaluation
      </h1>
      <EvaluationSkeleton />
    </div>
  </PageLayout>
);

const ErrorState = ({ message }: { message: string }) => (
  <PageLayout>
    <div className="flex min-h-[50vh] items-center justify-center">
      <p className="text-center text-stone-500 dark:text-stone-500">{message}</p>
    </div>
  </PageLayout>
);

const useDraft = (draftId: string | null) => {
  return useQuery({
    queryKey: ["shared-draft", draftId],
    queryFn: async (): Promise<Draft | null> => {
      if (!draftId) return null;

      const { data, error } = await supabase
        .from("drafts")
        .select("content, topic_id")
        .eq("id", draftId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!draftId,
  });
};

const useTopic = (topicId: string | null) => {
  return useQuery({
    queryKey: ["topic", topicId],
    queryFn: async (): Promise<Topic | null> => {
      if (!topicId) return null;

      const { data, error } = await supabase
        .from("topics")
        .select("*")
        .eq("id", topicId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!topicId,
  });
};

const SharedEssayContent = ({
  draftId
}: {
  draftId: string
}) => {
  const {
    data: evaluation,
    isLoading: isLoadingEvaluation,
    error: evaluationError
  } = useEvaluation(draftId);

  const {
    data: draft,
    isLoading: isLoadingDraft,
    error: draftError
  } = useDraft(draftId);

  const {
    data: topic,
    isLoading: isLoadingTopic
  } = useTopic(draft?.topic_id);

  if (isLoadingEvaluation || isLoadingDraft || isLoadingTopic) {
    return <LoadingState />;
  }

  if (evaluationError) {
    return <ErrorState message="Failed to load evaluation. Please try again later." />;
  }

  if (draftError) {
    return <ErrorState message="Failed to load essay content. Please try again later." />;
  }

  if (!evaluation || !draft) {
    return <ErrorState message="Essay not found or has been removed." />;
  }

  return (
    <PageLayout>
      {topic && (
        <div className="space-y-4 bg-white dark:bg-stone-800 p-6 rounded-lg border border-stone-200 dark:border-stone-700">
          <h2 className="text-xl font-semibold text-stone-800 dark:text-white">
            {topic.topic}
          </h2>
          <p className="text-stone-600 dark:text-stone-500 whitespace-pre-wrap">
            {topic.question}
          </p>
        </div>
      )}
      
      <h1 className="text-3xl font-semibold dark:text-white">
        Shared Essay Evaluation
      </h1>
      <EvaluationResults
        evaluation={evaluation}
        essayContent={draft.content}
      />
    </PageLayout>
  );
};

const SharedEssay = () => {
  const { draftId } = useParams();

  if (!draftId) {
    return <ErrorState message="No essay ID provided." />;
  }

  return <SharedEssayContent draftId={draftId} />;
};

export default SharedEssay;
